/* =======================================================================
   Global counters and references
======================================================================== */

/**
 * Global counter for unique "Show more" button IDs
 */
let showMoreBtnCounter = 1;

/**
 * Reference to our active MutationObserver, if any
 */
let quickAddObserver = null;

/**
 * Reference to our interval check, if any
 */
let quickAddInterval = null;

/* =======================================================================
   DOMContentLoaded => attach click events
======================================================================== */

document.addEventListener('DOMContentLoaded', function() {
  const quickAddButtons = document.querySelectorAll('.js-quick-add');
  if (!quickAddButtons.length) return;

  quickAddButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      // Each time we click a new product's .js-quick-add,
      // we do a fresh initialization
      handleQuickAddClick();
    });
  });
});

/* =======================================================================
   On quick-add click => set up logic for new product
======================================================================== */

function handleQuickAddClick() {
  // 1) Disconnect old observer/interval, if any
  if (quickAddObserver) {
    quickAddObserver.disconnect();
    quickAddObserver = null;
  }
  if (quickAddInterval) {
    clearInterval(quickAddInterval);
    quickAddInterval = null;
  }

  // 2) Optionally clear old fieldsets from the drawer to ensure we
  //    don't mis-detect leftover data from product X
  const drawer = document.querySelector('.quick-add-drawer');
  if (drawer) {
    const oldFieldsets = drawer.querySelectorAll('fieldset.option-selector');
    oldFieldsets.forEach(fs => fs.remove());
  }

  // 3) Start observing and also set up an interval check
  observeQuickAddDrawer();
  repeatedFieldsetCheck();
}

/* =======================================================================
   MutationObserver + repeated interval checking
======================================================================== */

/**
 * Sets up a mutation observer that watches for new fieldsets inside .quick-add-drawer
 */
function observeQuickAddDrawer() {
  const drawer = document.querySelector('.quick-add-drawer');
  if (!drawer) {
    return;
  }

  quickAddObserver = new MutationObserver((mutations, obs) => {
    if (fieldsetsAreReady(drawer)) {
      finalizeInitialization(drawer, obs);
    }
  });

  quickAddObserver.observe(drawer, {
    childList: true,
    subtree: true,
  });
}

/**
 * Also set up a short interval check in case the theme does
 * partial updates not caught by the observer or instantly replaced fieldsets.
 */
function repeatedFieldsetCheck() {
  const drawer = document.querySelector('.quick-add-drawer');
  if (!drawer) return;

  let previousCount = 0;

  quickAddInterval = setInterval(() => {
    const fsCount = drawer.querySelectorAll('fieldset.option-selector').length;
    // If we detect new fieldsets or see that they're now present
    if (fsCount > previousCount && fsCount > 0) {
      finalizeInitialization(drawer);
    }
    previousCount = fsCount;
  }, 200); // check every 200ms
}

/**
 * If we confirm we have fieldsets, run measure reorder + show-more logic,
 * disconnect observer and interval so we don't run multiple times.
 */
function finalizeInitialization(drawer, observer) {
  if (observer) {
    observer.disconnect();
    quickAddObserver = null;
  }
  if (quickAddInterval) {
    clearInterval(quickAddInterval);
    quickAddInterval = null;
  }
  initializeDrawerContent(drawer);
}

/**
 * Simple check if there's at least one fieldset present
 * (and possibly > 0 variant labels).
 */
function fieldsetsAreReady(drawer) {
  const fsets = drawer.querySelectorAll('fieldset.option-selector');
  return fsets.length > 0;
}

/* =======================================================================
   initializeDrawerContent => reorder + limitVariantOptions
======================================================================== */

function initializeDrawerContent(drawer) {
  autoReorderMeasureFieldsets(drawer);
  limitVariantOptions(drawer);
}

/* =======================================================================
   Reorder Measure Options
======================================================================== */

function parseWidthHeight(text) {
  const regex = /(\d+)\s*x\s*(\d+)/i;
  const match = regex.exec(text);
  if (!match) return null;

  const width = parseFloat(match[1]);
  const height = parseFloat(match[2]);
  return {
    width,
    height,
    area: width * height
  };
}

/**
 * Reorders measure-like fieldsets if at least half the inputs parse "width x height"
 */
function autoReorderMeasureFieldsets(container) {
  const fieldsets = container.querySelectorAll('fieldset.option-selector');
  fieldsets.forEach(fieldset => {
    const btnContainer = fieldset.querySelector('.option-selector__btns');
    if (!btnContainer) return;

    const radios = btnContainer.querySelectorAll('input[type="radio"].opt-btn');
    if (!radios.length) return;

    let parseCount = 0;
    const items = Array.from(radios).map(input => {
      const label = btnContainer.querySelector(`label[for="${input.id}"]`);
      const parsed = parseWidthHeight(input.value);
      if (parsed && parsed.area) parseCount++;
      return { input, label, parsed };
    });

    const threshold = items.length / 2;
    if (parseCount < threshold) return;

    // reorder by ascending area
    items.sort((a, b) => {
      const areaA = a.parsed?.area ?? Infinity;
      const areaB = b.parsed?.area ?? Infinity;
      return areaA - areaB;
    });

    // re-append
    items.forEach(({ input, label }) => {
      if (input) input.remove();
      if (label) label.remove();
      if (input) btnContainer.appendChild(input);
      if (label) btnContainer.appendChild(label);
    });
  });
}

/* =======================================================================
   Limit Variant Options ("Show more / Show less")
======================================================================== */

function limitVariantOptions(container) {
  const VISIBLE_ITEM_COUNT = 10;

  const showMoreInput = container.querySelector('#product-option-selector-show-more-label');
  const showLessInput = container.querySelector('#product-option-selector-show-less-label');
  const showMoreText = showMoreInput?.value || 'Show more';
  const showLessText = showLessInput?.value || 'Show less';

  const fieldsets = container.querySelectorAll('fieldset.option-selector');
  fieldsets.forEach(fieldset => {
    const labels = Array.from(fieldset.querySelectorAll('.option-selector__btns label.opt-label'));

    const enabledLabels = labels.filter(label => {
      const inputId = label.getAttribute('for');
      if (!inputId) return false;
      const input = fieldset.querySelector(`#${inputId}`);
      if (!input) return false;

      const disabledClass = input.classList.contains('disabled');
      const disabledAttr = input.disabled;
      return !(disabledClass || disabledAttr);
    });

    if (enabledLabels.length > VISIBLE_ITEM_COUNT) {
      let index = 0;
      labels.forEach(label => {
        if (enabledLabels.includes(label)) {
          if (index < VISIBLE_ITEM_COUNT) {
            label.classList.remove('custom-hidden');
          } else {
            label.classList.add('custom-hidden');
          }
          index++;
        } else {
          label.classList.remove('custom-hidden'); // keep disabled visible
        }
      });

      let showMoreBtnContainer = fieldset.nextElementSibling;
      if (!showMoreBtnContainer?.classList.contains('show-more-btn-container')) {
        showMoreBtnContainer = fieldset.parentElement.querySelector('.show-more-btn-container');
      }
      if (!showMoreBtnContainer) {
        return;
      }

      // remove any existing .show-more-btn to start fresh
      const existingBtn = showMoreBtnContainer.querySelector('.show-more-btn');
      if (existingBtn) existingBtn.remove();

      const showMoreBtn = document.createElement('button');
      showMoreBtn.classList.add('show-more-btn', 'text-decoration--underline');
      showMoreBtn.textContent = showMoreText;
      showMoreBtn.id = 'show-more-btn-' + showMoreBtnCounter;
      showMoreBtnCounter++;

      showMoreBtnContainer.appendChild(showMoreBtn);

      // toggle logic
      showMoreBtn.addEventListener('click', () => {
        const isExpanded = (showMoreBtn.textContent === showLessText);
        if (isExpanded) {
          let idx = 0;
          enabledLabels.forEach(label => {
            if (idx >= VISIBLE_ITEM_COUNT) label.classList.add('custom-hidden');
            idx++;
          });
          showMoreBtn.textContent = showMoreText;
          setShowMoreState(showMoreBtn.id, false);
          window.scrollTo({ top: 0, behavior: 'smooth' });

        } else {
          enabledLabels.forEach(label => label.classList.remove('custom-hidden'));
          showMoreBtn.textContent = showLessText;
          setShowMoreState(showMoreBtn.id, true);
        }
      });

      // apply saved expand/collapse
      const expanded = getShowMoreState(showMoreBtn.id);
      if (expanded) {
        enabledLabels.forEach(label => label.classList.remove('custom-hidden'));
        showMoreBtn.textContent = showLessText;
      } else {
        let idx = 0;
        enabledLabels.forEach(label => {
          if (idx >= VISIBLE_ITEM_COUNT) label.classList.add('custom-hidden');
          idx++;
        });
        showMoreBtn.textContent = showMoreText;
      }

    } else {
      // <= 10 => show all
      labels.forEach(label => label.classList.remove('custom-hidden'));

      let showMoreBtnContainer = fieldset.nextElementSibling;
      if (!showMoreBtnContainer?.classList.contains('show-more-btn-container')) {
        showMoreBtnContainer = fieldset.parentElement.querySelector('.show-more-btn-container');
      }
      if (showMoreBtnContainer) {
        const oldBtn = showMoreBtnContainer.querySelector('.show-more-btn');
        if (oldBtn) oldBtn.classList.add('hidden');
      }
    }
  });
}

/**
 * Save expand/collapse state
 */
function setShowMoreState(buttonId, isExpanded) {
  localStorage.setItem(buttonId, isExpanded ? 'true' : 'false');
}

/**
 * Retrieve expand/collapse state
 */
function getShowMoreState(buttonId) {
  return (localStorage.getItem(buttonId) === 'true');
}